import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiError, type ReportCatalogEntryView, type ReportCatalogView, type ReportPreviewView } from '@vello/api-client';
import { Button, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextInput, type Column, Cluster, Stack, Text, FieldCell } from '@vello/web-theme';
import {
  REPORT_FILTER_CONTROLS,
  cleanFilters,
  countryOfMarket,
  customPeriod,
  defaultDelimiter,
  formatMarketInstant,
  marketDate,
  ridesReportsAuthority,
  scopeTimeZone,
} from '../../src/index';
import { useAdmin } from '../session';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { LoadingBlock } from '../components/loading-block';
import { ExportDialog, type ExportTarget } from '../components/bi/ExportDialog';
import { useCanExport } from '../components/bi/bi-exports-store';

/**
 * Relatórios › Catálogo (W9-RIDES-REPORTS-AUDIT, D472, ADR-0090; DevMobility H-042, H-043, H-044, I-051, C-007; X-ray P-07):
 * the closed catalogue of operator reports. The operator picks a report, a scope (one market, or every market of a country),
 * a period of whole local days when the report has one, the report's own filters and — for the file — the CSV delimiter
 * (`;` for a spreadsheet in a language that writes decimals with a comma). PREVIEW shows the first rows; DOWNLOAD asks the
 * server for the CSV (UTF-8 with a BOM, UTC and local-time columns, money as integer minor units beside its currency, ids and
 * never names), which the server audits BEFORE it releases the file. Read only for a viewer without `report:export`.
 */
const DAY_MS = 86_400_000;

type Values = Readonly<Record<string, string | number | null>>;
/** A preview row with its position (the key of the list: a report row has no natural id). */
interface Row { readonly i: number; readonly v: Values }

export function ReportCatalogScreen(): ReactElement {
  const canExportBackground = useCanExport();
  const [background, setBackground] = useState<ExportTarget | undefined>(undefined);
  const { runtime, scope, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, explain, reason, has } = useVocab();
  const place = usePlaceNames();
  const authority = ridesReportsAuthority(grants);
  const zone = scopeTimeZone(scope.current, markets);

  const [catalog, setCatalog] = useState<ReportCatalogView | undefined>(undefined);
  const [reportId, setReportId] = useState('');
  const { market, choices } = useScopeMarket();
  // The report's scope IS the header's (no second selector): the scoped market, or — when the header names a country
  // covering several markets — that whole country. A global scope spanning countries names neither: the notice shows.
  const scopeValue = useMemo(() => {
    if (market !== undefined) return `market:${market}`;
    // F3 (ADR-0124): a state is asked as itself (the markets that carry it) - never as its whole country.
    if (scope.current.region !== undefined) return choices.length > 0 ? `region:${scope.current.region}` : '';
    const countries = [...new Set(choices.map((c) => countryOfMarket(c.id)))];
    return countries.length === 1 ? `country:${countries[0]}` : '';
  }, [market, choices, scope]);
  const [fromDay, setFromDay] = useState(() => marketDate(new Date(Date.now() - 6 * DAY_MS), zone));
  const [toDay, setToDay] = useState(() => marketDate(new Date(), zone));
  const [filterValues, setFilterValues] = useState<Readonly<Record<string, string>>>({});
  const [delimiter, setDelimiter] = useState<'comma' | 'semicolon'>(defaultDelimiter(lang));
  const [preview, setPreview] = useState<ReportPreviewView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'warning'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (error: ApiError): string => explain(error) ?? t('rrep.err.generic', { code: fail(error) }),
    [t, fail, explain],
  );

  useEffect(() => {
    void (async () => {
      const res = await runtime.ops.reportCatalog();
      if (res.ok) {
        setCatalog(res.value);
        setReportId((cur) => (cur === '' ? (res.value.reports[0]?.id ?? '') : cur));
      } else setError(refuse(res.error));
    })();
  }, [runtime, refuse]);

  const report: ReportCatalogEntryView | undefined = catalog?.reports.find((r) => r.id === reportId);

  /** The request the operator described, or `undefined` while it is incomplete. */
  const request = (): { readonly market?: string; readonly region?: string; readonly country?: string; readonly from?: string; readonly to?: string; readonly filters: Readonly<Record<string, string>> } | undefined => {
    if (report === undefined || scopeValue === '') return undefined;
    // `market:zz:test` / `region:ZZ-AA` / `country:ZZ` — the market id itself contains a colon, so split on the FIRST one only.
    const cut = scopeValue.indexOf(':');
    const kind = scopeValue.slice(0, cut);
    const id = scopeValue.slice(cut + 1);
    const period = report.period ? customPeriod(fromDay, toDay, zone) : undefined;
    if (report.period && period === undefined) return undefined;
    return {
      ...(kind === 'market' ? { market: id } : kind === 'region' ? { region: id } : { country: id }),
      ...(period !== undefined ? { from: period.from, to: period.to } : {}),
      filters: cleanFilters(filterValues, report.filters),
    };
  };

  const runPreview = async (): Promise<void> => {
    const req = request();
    if (report === undefined) return;
    if (req === undefined) {
      setError(t('rrep.err.period'));
      return;
    }
    const seq = ++readSeq.current;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.reportPreview(report.id, req);
    if (seq !== readSeq.current) return;
    setBusy(false);
    if (res.ok) {
      setPreview(res.value);
      setError(undefined);
    } else {
      setPreview(undefined);
      setError(refuse(res.error));
    }
  };

  const download = async (): Promise<void> => {
    const req = request();
    if (report === undefined) return;
    if (req === undefined) {
      setError(t('rrep.err.period'));
      return;
    }
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.reportExport(report.id, { ...req, delimiter });
    setBusy(false);
    if (!res.ok) {
      setError(refuse(res.error));
      return;
    }
    setError(undefined);
    // The server's CSV already starts with its UTF-8 byte-order mark: the file is written exactly as received.
    const blob = new Blob([res.value.csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = res.value.filename;
    a.click();
    URL.revokeObjectURL(url);
    setNotice({ tone: res.value.truncated ? 'warning' : 'success', text: t(res.value.truncated ? 'rrep.report.exportedTruncated' : 'rrep.report.exported', { rows: res.value.rows, file: res.value.filename }) });
  };

  const optionLabel = (key: string, value: string): string => {
    switch (key) {
      case 'status':
        return v('ride.status', value);
      case 'cancelledBy':
        return v('ride.actor', value);
      case 'stage':
        return v('ride.cancel_stage', value);
      case 'offerStatus':
        return v('dispatch.offer_status', value);
      case 'direction':
        return v('ride.final_fare_direction', value === 'UP' ? 'UP' : value === 'DOWN' ? 'DOWN' : value);
      case 'category':
        return v('ride.category', value);
      case 'payment':
        return v('ride.payment_method', value);
      default:
        return v('ops.report_filter_key', value);
    }
  };

  /** The vocabulary that words a coded cell of a report, by its column. */
  const CELL_VOCABULARY: Readonly<Record<string, string>> = {
    status: 'ride.status', ride_status: 'ride.status', category: 'ride.category', payment_method: 'ride.payment_method',
    cancelled_by: 'ride.actor', stage: 'ride.cancel_stage', offer_status: 'dispatch.offer_status',
    channel: 'dispatch.offer_channel', reject_reason: 'dispatch.offer_reject_reason', driver_status: 'driver.status', became_ride: 'ops.report_yes_no',
  };
  const cell = (key: string, kind: string, value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    if (kind === 'instant') return formatMarketInstant(String(value), zone, lang);
    if (kind !== 'code') return String(value);
    const text = String(value);
    if (key === 'market_id') return place.market(text);
    if (key === 'currency') return text;
    if (key === 'cancellation_reason') return v(has('ops.cancel_reason', text) ? 'ops.cancel_reason' : 'ride.cancel_reason', text);
    if (key === 'reason_code') return has('ops.cancel_reason', text) ? v('ops.cancel_reason', text) : has('ride.cancel_reason', text) ? v('ride.cancel_reason', text) : reason(text);
    const vocabulary = CELL_VOCABULARY[key];
    return v(vocabulary ?? 'ops.report_column', key === 'driver_status' ? text.toUpperCase() : text);
  };

  const columns: readonly Column<Row>[] = (preview?.columns ?? []).map((col) => ({
    key: col.key,
    header: v('ops.report_column', col.key),
    numeric: col.kind === 'money_minor' || col.kind === 'integer' || col.kind === 'seconds' || col.kind === 'bps',
    width: 'minmax(120px, 1fr)',
    render: (row: Row) => <Text role="caption" tone="secondary" mono={!(col.kind === 'text' || col.kind === 'code' || col.kind === 'instant')}>{cell(col.key, col.kind, row.v[col.key])}</Text>,
  }));

  return (
    <>
      <PageHeader title={t('rrep.report.title')} subtitle={t('rrep.report.subtitle')} />

      {!authority.mayExport && (
        <Notice tone="neutral">{t('rrep.report.readOnly')}</Notice>
      )}
      {error !== undefined && (
        <Notice tone="danger">{error}</Notice>
      )}
      {notice !== undefined && (
        <Notice tone={notice.tone}>{notice.text}</Notice>
      )}

      {catalog === undefined ? (
        <Panel>
          {error !== undefined ? <EmptyState title={t('rrep.report.unavailable')} /> : <LoadingBlock />}
        </Panel>
      ) : scopeValue === '' ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <Panel>
            <Stack gap={4}>
              <Cluster gap={4} wrap>
                <FieldCell size="lg">
                  <Field label={t('rrep.report.pick')} htmlFor="rrep-report" hint={report !== undefined ? t(`rrep.report.${report.id}.desc`) : undefined}>
                    <Select id="rrep-report" value={reportId} onChange={(e) => { setReportId(e.target.value); setFilterValues({}); setPreview(undefined); setNotice(undefined); }}>
                      {catalog.reports.map((r) => (
                        <option key={r.id} value={r.id}>{v('ops.report_id', r.id)}</option>
                      ))}
                    </Select>
                  </Field>
                </FieldCell>
                {report?.period === true && (
                  <>
                    <Field label={t('rrep.filter.from')} htmlFor="rrep-rfrom">
                      <TextInput id="rrep-rfrom" type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} />
                    </Field>
                    <Field label={t('rrep.filter.to')} htmlFor="rrep-rto">
                      <TextInput id="rrep-rto" type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />
                    </Field>
                  </>
                )}
                {(report?.filters ?? []).map((key) => {
                  const control = REPORT_FILTER_CONTROLS[key];
                  if (control === undefined) return null;
                  const id = `rrep-rf-${key}`;
                  return (
                    <FieldCell size="xs" key={key}>
                      <Field label={v('ops.report_filter_key', key)} htmlFor={id}>
                        {control.kind === 'select' ? (
                          <Select id={id} value={filterValues[key] ?? ''} onChange={(e) => setFilterValues({ ...filterValues, [key]: e.target.value })}>
                            <option value="">{t('rrep.rfilter.any')}</option>
                            {control.options.map((o) => (
                              <option key={o} value={o}>{optionLabel(key, o)}</option>
                            ))}
                          </Select>
                        ) : (
                          <TextInput
                            id={id}
                            {...(control.kind === 'number' ? { type: 'number', min: control.min, max: control.max, inputMode: 'numeric' as const } : { maxLength: 40 })}
                            value={filterValues[key] ?? ''}
                            onChange={(e) => setFilterValues({ ...filterValues, [key]: e.target.value })}
                          />
                        )}
                      </Field>
                    </FieldCell>
                  );
                })}
                {authority.mayExport && (
                  <FieldCell size="sm">
                    <Field label={t('rrep.report.delimiter')} htmlFor="rrep-delim" hint={t('rrep.report.delimiterHint')}>
                      <Select id="rrep-delim" value={delimiter} onChange={(e) => setDelimiter(e.target.value === 'semicolon' ? 'semicolon' : 'comma')}>
                        <option value="comma">{t('rrep.report.comma')}</option>
                        <option value="semicolon">{t('rrep.report.semicolon')}</option>
                      </Select>
                    </Field>
                  </FieldCell>
                )}
              </Cluster>
              <Cluster gap={3} wrap>
                <Button variant={authority.mayExport ? 'secondary' : 'primary'} icon="search" disabled={busy || scopeValue === ''} onClick={() => void runPreview()}>{t('rrep.report.preview')}</Button>
                {authority.mayExport && (
                  <Button variant="primary" icon="download" disabled={busy || scopeValue === ''} onClick={() => void download()}>{t('rrep.report.download')}</Button>
                )}
                {authority.mayExport && canExportBackground && (
                  <Button
                    variant="secondary"
                    icon="download"
                    disabled={busy || scopeValue === ''}
                    onClick={() => {
                      const req = request();
                      if (report === undefined) return;
                      if (req === undefined) {
                        setError(t('rrep.err.period'));
                        return;
                      }
                      setBackground({ source: { kind: 'CATALOG_REPORT', report: { reportId: report.id, ...req } }, title: t('bi.exp.dialog.title') });
                    }}
                  >
                    {t('bi.exportBackground')}
                  </Button>
                )}
              </Cluster>
              <Text as="p" role="caption" tone="secondary">{t('rrep.report.privacy', { rows: catalog.exportRows })}</Text>
            </Stack>
          </Panel>
          <ExportDialog open={background !== undefined} target={background} onClose={() => setBackground(undefined)} />

          {preview !== undefined && (
            <>
              {preview.truncated && (
                <Notice tone="neutral">{t('rrep.report.previewTruncated', { cap: preview.cap })}</Notice>
              )}
              <ListTable
                columns={columns}
                rows={preview.rows.map((v, i): Row => ({ i, v }))}
                rowKey={(r) => String(r.i)}
                empty={<EmptyState title={t('rrep.report.empty.title')} detail={t('rrep.report.empty.detail')} />}
                minWidth={Math.max(900, columns.length * 130)}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
